import { Controller } from '@hotwired/stimulus'

// REFERENCES
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tablist_role#examples
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tab_role

export default class extends Controller {
  static targets = ['tab', 'mobileTab', 'tablist', 'tabPanel', 'mobileDropdownButton']
  static values = {
    vertical: Boolean
  }

  connect() {
    this._resetTabFocus()
    if (this.tabFocus == null) {
      throw new Error('Cannot render a tablist without an active tab')
    }

    ;[...this.tabTargets, ...this.mobileTabTargets].forEach(tab => {
      tab.addEventListener('click', this.handleClick)
    })

    this.element.addEventListener('blur', e => console.log('BLUR'))

    this.tablistTargets.forEach(tablist => {
      tablist.addEventListener('keydown', this.handleKey)
      tablist.addEventListener('focusout', this.handleFocusOut)
    })

    this.tabPanelTargets.forEach(p => {
      p.addEventListener('turbo:frame-render', (e) => this.equalizeFrameHeights(e.srcElement))
    })

    removeEventListener('popstate', globalPopState)
    addEventListener('popstate', this.handlePopState)

    const initialTab = this.tabTargets.find(t => t.hasAttribute('aria-selected'))
    if (initialTab != null) {
      const initialPanel = this.element.querySelector(`#${initialTab.getAttribute('aria-controls')}`)
      this.equalizeFrameHeights(getPanelFrame(initialPanel))
      const frameWithPushUrl = initialPanel.querySelector(':scope > turbo-frame[data-tablist-push-url]')
      if (frameWithPushUrl) {
        const targetUrl = frameWithPushUrl.getAttribute('data-tablist-push-url')
        history.replaceState({ tablist: { pushUrl: targetUrl, tabPanelId: initialPanel.id } }, '', location.href)
      }
    }
  }

  disconnect() {
    removeEventListener('popstate', this.handlePopState)
    addEventListener('popstate', globalPopState)
    this.tablistTargets.forEach(tablist => {
      tablist.removeEventListener('keydown', this.handleKey)
    })
    this.tabTargets.forEach(tab => {
      tab.removeEventListener('click', this.handleClick)
    })
  }

  changeTabByName(event) {
    event.preventDefault()
    const tabName = event.params.name
    const targetTab = this.tabTargets.find(tab => tab.id === `tab-${tabName}`)

    if (!targetTab) {
      throw new Error(`Tab with name '${tabName}' not found. Available tabs: ${this.tabTargets.map(t => t.id).join(', ')}`)
    }

    this.changeTabs(targetTab, true)
  }

  changeTabs = (targetTab, pushState = false) => {
    // Remove all current selected tabs
    this.element.querySelectorAll('[aria-selected]').forEach(t => {
      t.removeAttribute('aria-selected')
      t.setAttribute('tabindex', -1)
      t.classList.remove('active')
    })

    this._activateTab(targetTab)
    // also activate mobile/desktop equivalent
    const isMobile = this.mobileTabTargets.includes(targetTab)
    if (isMobile) {
      this.tabFocus = this.mobileTabTargets.indexOf(targetTab)
      this._activateTab(this.tabTargets[this.tabFocus])
    } else {
      this.tabFocus = this.tabTargets.indexOf(targetTab)
      this._activateTab(this.mobileTabTargets[this.tabFocus])
    }

    this.mobileDropdownButtonTarget.innerHTML = targetTab.innerHTML

    // Hide all tab panels
    this.tabPanelTargets.forEach(p => p.setAttribute('hidden', true))

    // Show the selected panel
    const selectedPanel = this.element.querySelector(`#${targetTab.getAttribute('aria-controls')}`)
    if (selectedPanel == null) {
      throw new Error(`Missing target panel for tab '${targetTab.id}'`)
    }

    getPanelFrame(selectedPanel).style.minHeight = `${this.element.getAttribute('data-min-height')}px`

    selectedPanel.removeAttribute('hidden')
    if (pushState) {
      const frameWithPushUrl = selectedPanel.querySelector(':scope > turbo-frame[data-tablist-push-url]')
      if (frameWithPushUrl == null) return

      const targetUrl = frameWithPushUrl.getAttribute('data-tablist-push-url')
      history.pushState({ tablist: { pushUrl: targetUrl, tabPanelId: selectedPanel.id } }, '', targetUrl)
      document.dispatchEvent(
        new CustomEvent('tablist:change', { detail: { targetUrl: targetUrl, tabPanelId: selectedPanel.id } })
      )
    } else {
      document.dispatchEvent(new CustomEvent('tablist:change', { detail: { tabPanelId: selectedPanel.id } }))
    }
  }

  handleClick = e => this.changeTabs(e.currentTarget, true)

  handleKey = e => {
    const isMobile = this.mobileTabTargets.includes(e.target)
    const tabTargets = isMobile ? this.mobileTabTargets : this.tabTargets
    // Move right
    if (
      e.key === 'ArrowRight' ||
      e.key === 'ArrowLeft' ||
      (this.verticalValue && (e.key === 'ArrowUp' || e.key === 'ArrowDown'))
    ) {
      e.preventDefault()
      tabTargets[this.tabFocus].setAttribute('tabindex', -1)
      if (e.key === 'ArrowRight' || (this.verticalValue && e.key === 'ArrowDown')) {
        this.tabFocus++
        // If we're at the end, go to the start
        if (this.tabFocus >= tabTargets.length) {
          this.tabFocus = 0
        }
        // Move left
      } else if (e.key === 'ArrowLeft' || (this.verticalValue && e.key === 'ArrowUp')) {
        this.tabFocus--
        // If we're at the start, move to the end
        if (this.tabFocus < 0) {
          this.tabFocus = tabTargets.length - 1
        }
      }

      tabTargets[this.tabFocus].setAttribute('tabindex', 0)
      tabTargets[this.tabFocus].focus()
    }
  }

  handleFocusOut = e => {
    // only fire for a genuine focusout, i.e. the new focused element is outside the tablist
    if (!e.currentTarget.contains(e.relatedTarget)) {
      // set the briefest timeout to allow default behavior (tab to next focusable element) to happen first
      // this is not an issue in Chrome but was causing the system test to fail, which might indicate
      // issues in other browsers
      setTimeout(() => {
        // handle case of tabbing out while navigating using arrow keys
        // when focus returns, should be on active element
        const oldTabFocus = this.tabFocus
        this._resetTabFocus()
        if (this.tabFocus !== oldTabFocus) {
          ;[this.tabTargets, this.mobileTabTargets].forEach(tabs => {
            tabs[oldTabFocus].setAttribute('tabindex', -1)
            tabs[this.tabFocus].setAttribute('tabindex', 0)
          })
        }
      }, 1)
    }
  }

  handlePopState = e => {
    if (e.state.tablist) {
      const tab = this.element.querySelector(`[aria-controls="${e.state.tablist.tabPanelId}"]`)

      if (tab === null) {
        if (!e.state.tablist.pushUrl) return
        document.location = e.state.tablist.pushUrl
      } else {
        this.changeTabs(tab)
      }
    }
  }

  _activateTab = tab => {
    tab.setAttribute('aria-selected', '')
    tab.setAttribute('tabindex', 0)
    tab.classList.add('active')
  }

  _resetTabFocus = () => {
    this.tabFocus = this.tabTargets.findIndex(tab => {
      return tab.getAttribute('aria-selected') !== null
    })
  }

  // prevent toggling between tabs from messing with the browser's scroll position in a jarring way
  equalizeFrameHeights = (renderedFrame) => {
    const minHeight = parseFloat(this.element.getAttribute('data-min-height'))
    const newHeight = renderedFrame.offsetHeight
    if (!minHeight || newHeight > minHeight) {
      this.element.setAttribute('data-min-height', newHeight)
    } else {
      renderedFrame.style.minHeight = `${minHeight}px`
    }
  }
}

function getPanelFrame(panel) {
  return panel.querySelector(':scope > turbo-frame')
}

function globalPopState(e) {
  if (e.state && e.state.tablist) {
    document.location = e.state.tablist.pushUrl
  }
}

addEventListener('popstate', globalPopState)
